import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import PageHeader from '../../../shared/PageHeader';
import TaxBasisLine from '../../../shared/TaxBasisLine';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import { useScreenAccess } from '../../../shared/PermissionsContext';
import { formatDate, formatDateTime } from '../../../shared/tenantClock';
import { formatMoney, formatNumber } from '../../../shared/numberFormat';
import { formatUnitPrice } from '../../sales/Prices/priceLookupShared';
import { InvoiceQuickbooksPill, VoidPill } from './InvoicesTable';
import './InvoicePage.css';

// One Invoice (issue #269), read only: an Invoice is never edited once
// created. Its lines are exactly what shipped in its shipment, at the
// Sales Order's agreed prices, and its amounts read in the Customer's
// basis when it was created (Retail inc GST, Business ex). The order's
// shipping charge is on its first Invoice; a later one shows the line at
// $0.00, greyed, so the Customer can see they weren't charged. One that
// hasn't reached QuickBooks says why, with a Retry (issue #458).
const round2 = (n) => Math.round(Number(n) * 100 + Number.EPSILON) / 100;

const TH = { padding: '8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left', fontSize: 13 };
const TD = { padding: '6px 8px', borderBottom: '1px solid #eceef1' };
const fieldLabelStyle = { display: 'block', fontSize: 12, color: '#555', marginBottom: 4 };
const LINK_BUTTON = { background: 'none', border: 'none', color: '#1976d2', textDecoration: 'underline', cursor: 'pointer', fontSize: 13, padding: 0 };

function Field({ label, children }) {
  return (
    <div>
      <label style={fieldLabelStyle}>{label}</label>
      <div style={{ fontSize: 14 }}>{children || '—'}</div>
    </div>
  );
}

function InvoicePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const { canUpdate } = useScreenAccess();
  const [invoice, setInvoice] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`/api/invoices/${id}`)
      .then((res) => setInvoice(res.data.data))
      .catch((err) => setError(err.response?.data?.message || 'Failed to load the Invoice'));
  }, [id]);

  // The response is the Invoice as it now stands, so a second failure shows
  // its new reason.
  const retry = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`/api/invoices/${id}/quickbooks-retry`);
      setInvoice(res.data.data);
      dismissError();
    } catch (err) {
      showError(err, 'Retry failed');
    }
    setBusy(false);
  };

  if (error) return <div style={{ margin: 32 }}>{error}</div>;
  if (!invoice) return <div style={{ margin: 32 }}>Loading...</div>;

  const retail = invoice.pricing_basis === 'retail';
  const rate = Number(invoice.gst_rate) || 0;
  const gstApplicable = rate > 0;
  const basis = gstApplicable ? (retail ? 'inc GST' : 'ex GST') : null;
  const basisSuffix = basis ? ` (${basis})` : '';
  // The shipping line as the totals add it up (pricingBasis.documentTotals):
  // Shipping and Options each to the cent inc GST for Retail, ex for
  // Business.
  const shippingEx = Number(invoice.shipping_cost) + Number(invoice.options_cost);
  const shippingShown = retail
    ? round2(Number(invoice.shipping_cost) * (1 + rate / 100)) + round2(Number(invoice.options_cost) * (1 + rate / 100))
    : shippingEx;
  const unitShown = (l) => (retail && l.unit_price_inc !== null ? l.unit_price_inc : l.unit_price);
  const amountShown = (l) => (retail ? l.extended_price_inc_gst : l.extended_price);
  const isVoid = invoice.status === 'void';
  const voidedBy = invoice.voided_by ? (invoice.voided_by.name || invoice.voided_by.email) : null;
  const shipmentLink = invoice.shipment && invoice.sales_order
    ? `/warehouse-shipments?order_type=sales_order&order_id=${invoice.sales_order.id}&order_number=${encodeURIComponent(invoice.sales_order.display_number)}`
    : null;

  return (
    <div className="invoice-page" style={{ width: '100%', maxWidth: 960, margin: '32px auto 48px', padding: '0 24px', boxSizing: 'border-box' }}>
      <PageHeader
        crumbs={[{ label: 'Invoices', onClick: () => navigate('/receivables-invoices') }, { label: invoice.display_number }]}
        title={(
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            <span style={{ textDecoration: isVoid ? 'line-through' : 'none' }}>Invoice {invoice.display_number}</span>
            {isVoid && <VoidPill />}
          </span>
        )}
        subtitle={invoice.customer?.name}
      />

      {isVoid && (
        <div style={{ fontSize: 13, color: '#5a6b85', background: '#eef1f5', border: '1px solid #d5dbe3', borderRadius: 6, padding: '8px 12px', marginBottom: 16 }}>
          {[`Voided ${formatDateTime(invoice.voided_at)}`, voidedBy ? `by ${voidedBy}` : null, invoice.void_reason].filter(Boolean).join(' — ')}
        </div>
      )}

      {invoice.quickbooks?.state === 'failed' && (
        <div style={{ fontSize: 13, color: '#b91c1c', background: '#fdecec', border: '1px solid #f5c2c2', borderRadius: 6, padding: '8px 12px', marginBottom: 16 }}>
          <span style={{ display: 'inline-flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <span><strong>Not in QuickBooks:</strong> {invoice.quickbooks.message}</span>
            {canUpdate && (
              <button type="button" onClick={retry} disabled={busy} title="Sends this Invoice to QuickBooks again" style={LINK_BUTTON}>
                {busy ? 'Sending...' : 'Retry'}
              </button>
            )}
          </span>
        </div>
      )}

      <div style={{ background: '#fff', border: '1px solid #b3bbc7', borderRadius: 10, padding: 20, marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 16 }}>
          <Field label="Invoice Date">{formatDate(invoice.invoice_date)}</Field>
          <Field label="Due Date">{formatDate(invoice.due_date)}</Field>
          <Field label="Payment Terms">{invoice.payment_term_name}</Field>
          <Field label="Sales Order">
            {invoice.sales_order && (
              <button type="button" className="link-button" onClick={() => navigate(`/sales-orders/${invoice.sales_order.id}`)}>
                {invoice.sales_order.display_number}
              </button>
            )}
          </Field>
          <Field label="Shipment">
            {invoice.shipment && (shipmentLink ? (
              <button type="button" className="link-button" onClick={() => navigate(shipmentLink)}>{invoice.shipment.display_number}</button>
            ) : invoice.shipment.display_number)}
          </Field>
          <Field label="Purchase Order Reference">{invoice.po_reference}</Field>
          <Field label="QuickBooks">
            {invoice.quickbooks ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <InvoiceQuickbooksPill invoice={invoice} />
                {invoice.quickbooks.url && (
                  <a href={invoice.quickbooks.url} target="_blank" rel="noopener noreferrer" title="Opens this Invoice in QuickBooks" style={{ fontSize: 13 }}>Open</a>
                )}
              </span>
            ) : 'Not copied'}
          </Field>
        </div>
      </div>

      <div style={{ background: '#fff', border: '1px solid #b3bbc7', borderRadius: 10, padding: 20 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr>
              <th style={TH}>SKU</th>
              <th style={TH}>Description</th>
              <th style={TH}>Unit</th>
              <th style={{ ...TH, textAlign: 'right' }}>Qty</th>
              <th style={{ ...TH, textAlign: 'right' }}>Unit Price<TaxBasisLine>{basis}</TaxBasisLine></th>
              <th style={{ ...TH, textAlign: 'right' }}>Amount<TaxBasisLine>{basis}</TaxBasisLine></th>
            </tr>
          </thead>
          <tbody>
            {invoice.lines.map((l) => (
              <tr key={l.id}>
                <td style={{ ...TD, fontWeight: 600 }}>{l.sku || '—'}</td>
                <td style={TD}>{l.name || '—'}</td>
                <td style={TD}>{l.uom || '—'}</td>
                <td style={{ ...TD, textAlign: 'right' }}>{formatNumber(l.qty, { maxDecimals: 4 })}</td>
                <td style={{ ...TD, textAlign: 'right' }}>{formatUnitPrice(unitShown(l))}</td>
                <td style={{ ...TD, textAlign: 'right' }}>{formatMoney(amountShown(l) ?? 0)}</td>
              </tr>
            ))}
            <tr style={{ color: invoice.shipping_charged ? undefined : '#a0a8b5' }}>
              <td style={TD} />
              <td style={TD}>Shipping</td>
              <td style={TD} />
              <td style={TD} />
              <td style={TD} />
              <td style={{ ...TD, textAlign: 'right' }}>{formatMoney(shippingShown)}</td>
            </tr>
          </tbody>
        </table>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <div style={{ minWidth: 260 }}>
            {/* The lines and the shipping line above, added up. */}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, padding: '4px 0' }}>
              <span>Subtotal{basisSuffix}</span>
              <span>{formatMoney(round2(Number(invoice.subtotal) + shippingShown))}</span>
            </div>
            {Number(invoice.discount_amount) > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, padding: '4px 0' }}>
                <span>{invoice.discount_percent !== null ? `Discount (${Number(invoice.discount_percent)}%)` : 'Discount'}{basisSuffix}</span>
                <span>{`-${formatMoney(invoice.discount_amount)}`}</span>
              </div>
            )}
            {gstApplicable && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, padding: '4px 0' }}>
                <span>{retail ? 'Includes GST' : 'GST'}</span>
                <span>{formatMoney(invoice.gst_amount)}</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 16, fontWeight: 700, padding: '8px 0', borderTop: '1px solid #b3bbc7', marginTop: 4 }}>
              <span>Total{gstApplicable ? ' (inc GST)' : ''}</span>
              <span>{formatMoney(invoice.total)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default InvoicePage;
